import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { PharmacySegment } from '../../../../pages/admin-dashboard/pharmacy-segment/pharmacy-segment.model';
import { PharmacySegmentService } from '../../../../services/pharmacy-segment/pharmacy-segment.service';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { AddIcon } from '../../../../shared/ui/icons/add-icon/add-icon';
import { PharmacySegmentFilter } from './pharmacy-segment-filter/pharmacy-segment-filter';
import { PharmacySegmentTable } from './pharmacy-segment-table/pharmacy-segment-table';

@Component({
  selector: 'app-pharmacy-segment-list-component',
  standalone: true,
  imports: [Pagination, AddIcon, PharmacySegmentFilter, PharmacySegmentTable],
  templateUrl: './pharmacy-segment-list-component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PharmacySegmentListComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private segmentService = inject(PharmacySegmentService);

  private loadSub?: Subscription;

  //  Pagination State
  currentPage = signal(1);
  itemsPerPage = signal(25);

  // Filter State
  titleFilter = signal('');
  paymentTermFilter = signal('');
  createdOnFrom = signal('');
  createdOnTo = signal('');
  modifiedOnFrom = signal('');
  modifiedOnTo = signal('');
  orderBy = signal<string>('EXTRA_DISCOUNT');
  orderDir = signal<'ASC' | 'DESC'>('ASC');

  appliedFilters = signal({
    title: '',
    paymentTerm: '',
    createdOnFrom: '',
    createdOnTo: '',
    modifiedOnFrom: '',
    modifiedOnTo: '',
  });

  isFiltersApplied = computed(() => {
    const f = this.appliedFilters();
    return !!(
      f.title || f.paymentTerm || f.createdOnFrom || f.createdOnTo || f.modifiedOnFrom || f.modifiedOnTo
    );
  });

  // Data (from API)
  segments = signal<PharmacySegment[]>([]);
  totalItems = signal(0);

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
  }

  // API
  loadData(): void {
    this.loadSub?.unsubscribe();

    const f = this.appliedFilters();

    this.loadSub = this.segmentService
      .getSegments({
        pharmacySegmentTitle: f.title,
        createdOnFrom: f.createdOnFrom,
        createdOnTo: f.createdOnTo,
        lastModifiedOnFrom: f.modifiedOnFrom,
        lastModifiedOnTo: f.modifiedOnTo,
        orderBy: this.orderBy(),
        orderDir: this.orderDir(),
        pageNum: this.currentPage() - 1,
        pageSize: this.itemsPerPage(),
        // TODO: backend has no payment-term filter param, so f.paymentTerm is not sent
      })
      .subscribe({
        next: (res) => {
          this.segments.set(res.list);
          this.totalItems.set(res.total);
        },
        error: (err) => {
          console.error('Failed to load pharmacy segments', err.status, err.error);
          this.segments.set([]);
          this.totalItems.set(0);
        },
      });
  }

  // Filter Actions
  applyFilters(): void {
    this.appliedFilters.set({
      title: this.titleFilter(),
      paymentTerm: this.paymentTermFilter(),
      createdOnFrom: this.createdOnFrom(),
      createdOnTo: this.createdOnTo(),
      modifiedOnFrom: this.modifiedOnFrom(),
      modifiedOnTo: this.modifiedOnTo(),
    });
    this.currentPage.set(1);
    this.loadData();
  }

  resetFilters(): void {
    this.titleFilter.set('');
    this.paymentTermFilter.set('');
    this.createdOnFrom.set('');
    this.createdOnTo.set('');
    this.modifiedOnFrom.set('');
    this.modifiedOnTo.set('');
    this.orderBy.set('EXTRA_DISCOUNT');
    this.orderDir.set('ASC');
    this.itemsPerPage.set(25);
    this.appliedFilters.set({
      title: '',
      paymentTerm: '',
      createdOnFrom: '',
      createdOnTo: '',
      modifiedOnFrom: '',
      modifiedOnTo: '',
    });
    this.currentPage.set(1);
    this.loadData();
  }

  toggleSortOrder(): void {
    this.orderDir.update((value) => (value === 'ASC' ? 'DESC' : 'ASC'));
    this.currentPage.set(1);
    this.loadData();
  }

  //Pagination
  totalPages = computed(() => Math.ceil(this.totalItems() / this.itemsPerPage()) || 1);

  startItem = computed(() =>
    this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1
  );

  endItem = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  getPageNumbers(): number[] {
    const pages: number[] = [];
    for (let i = 1; i <= this.totalPages(); i++) {
      pages.push(i);
    }
    return pages;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages() && page !== this.currentPage()) {
      this.currentPage.set(page);
      this.loadData();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
      this.loadData();
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
      this.loadData();
    }
  }

  onPageSizeChange(newSize: number): void {
    this.itemsPerPage.set(newSize);
    this.currentPage.set(1);
    this.loadData();
  }

  onAddSegment(): void {
    this.router.navigate(['create'], { relativeTo: this.route });
  }

  onEditSegment(segment: PharmacySegment): void {
    this.router.navigate(['edit', segment.id], { relativeTo: this.route });
  }
}